import React, { FC, HTMLProps, ReactNode, useMemo } from "react";
import cn from "classnames";
import stl from "./index.module.scss";
import { Activity } from "../../store/models";
import { useStore } from "effector-react";
import { SelectedActivity } from "../../store/selected/selectedActivities";
import { humanizeMinutes, isPresent } from "../../utils";
import {
  Button,
  DefaultImage,
  Divider,
  Tag,
  Tooltip,
  Skeleton,
  Money,
} from "../../components";
import { useTranslation } from "react-i18next";
import { BsPencil, BsTrashFill, BsClock } from "react-icons/bs";
import { $currentLocale } from "../../store/shared/locales";
import { AppConfig } from "../../config";
import { Dinero } from "dinero.js";
import CheckIco from "../../assets/iconsComponents/Check";
import { usePageSize } from "../../hooks";

const tKeyBase = AppConfig.translationFileNames.base;
const tKeyStepMain = AppConfig.translationFileNames.step_Main;

interface Props extends Omit<HTMLProps<HTMLDivElement>, "data" | "onSelect"> {
  data: Activity;
  onEdit?: (data: Activity) => void;
  onDelete?: (selectedData: SelectedActivity) => void;
  price?: {
    min: Dinero;
    max: Dinero;
  };
  pricePending?: boolean;
  disabled?: boolean;

  selectedData?: SelectedActivity;
}
const PackageCard: FC<Props> = ({
  data,
  price,
  pricePending,
  disabled,
  onEdit,
  onDelete,
  selectedData,
  ...props
}) => {
  const pageSize = usePageSize();
  const { dateFnsLocale: locale } = useStore($currentLocale);
  const { t } = useTranslation([tKeyBase, tKeyStepMain]);
  const selected = isPresent(selectedData);

  const { image, description, name } = data;

  const remove = () => {
    if (!disabled && selectedData) onDelete?.(selectedData);
  };

  const edit = () => {
    if (!disabled) onEdit?.(data);
  };

  const renderPrice = (
    min?: Dinero,
    max?: Dinero,
    selectedPrice?: Dinero,
    pending?: boolean
  ) => {
    if (pending) {
      return <Skeleton width={50} className={stl.price} />;
    }
    if (isPresent(selectedPrice)) {
      return (
        <h4 className={stl.price}>
          <Money value={selectedPrice} />
        </h4>
      );
    }

    let priceNumbers: ReactNode = "";
    if (isPresent(min) && isPresent(max)) {
      if (min.hasSameAmount(max)) {
        priceNumbers = <Money value={min} />;
      } else {
        priceNumbers = (
          <>
            <Money value={min} />
            {" - "}
            <Money value={max} />
          </>
        );
      }
    }

    return (
      <>
        <h4 className={stl.price}>{priceNumbers}</h4>
        <span style={{ marginLeft: 5 }} onClick={(e) => e.stopPropagation()}>
          <Tooltip.Info>{t(`${tKeyStepMain}:varyPrices`)}</Tooltip.Info>
        </span>
      </>
    );
  };

  const duration = useMemo<string | null>(() => {
    if (!selectedData) return null;
    return humanizeMinutes(selectedData.timeSlots * data.slot_duration, locale);
  }, [selectedData, data.slot_duration, locale]);

  return (
    <div
      {...props}
      className={cn(stl.block, {
        [stl.selected]: selected,
        [stl.disabled]: disabled,
      })}
      tabIndex={0}
      onClick={edit}
      onKeyPress={({ key }) => {
        if (key === " " || key === "Enter") edit();
      }}
    >
      <CheckIco className={stl.check} />
      {image ? (
        <span className={stl.imgWrap}>
          <img src={image} alt="" />
        </span>
      ) : (
        <DefaultImage square />
      )}

      <h3 className={stl.name}>{name}</h3>

      <div className={stl.priceWrap}>
        {renderPrice(
          price?.min,
          price?.max,
          selectedData?.availabilitySlot.totalPrice,
          pricePending
        )}
      </div>

      {description && <p>{description}</p>}

      {selected && (
        <Tag variant={"primaryFilled"} icon={<BsClock />}>
          {duration}
        </Tag>
      )}

      {selected && (
        <>
          <Divider style={{ marginTop: 5 }} />

          {selectedData?.personTypes.map(({ name, quantity }) => (
            <Tag key={name} variant={"secondary"}>
              {name} x {quantity}
            </Tag>
          ))}
        </>
      )}

      {selected && (
        <div className={stl.actions}>
          <div>
            <Button
              icon={<BsPencil />}
              variant={"action"}
              hideContent={!pageSize.md}
              color={"editTransparent"}
              size={"small"}
              onClick={(e) => {
                e.stopPropagation();
                edit();
              }}
            >
              {t(`${tKeyBase}:actions.edit`)}
            </Button>
          </div>
          <div>
            <Button
              icon={<BsTrashFill />}
              variant={"action"}
              hideContent={!pageSize.md}
              color={"deleteTransparent"}
              size={"small"}
              onClick={(e) => {
                e.stopPropagation();
                remove();
              }}
            >
              {t(`${tKeyBase}:actions.remove`)}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackageCard;
